import { errorMessage } from '../lib/errors';
import { useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import type { SignInStep } from '../auth/types';
import { useServices } from '../services';

type Mode = 'sign_in' | 'mfa' | 'new_password' | 'forgot' | 'reset';

export function SignInPage() {
  const { auth, mock } = useServices();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? '/';

  const [mode, setMode] = useState<Mode>('sign_in');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [mfaHint, setMfaHint] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const handleStep = (step: SignInStep) => {
    if (step.step === 'done') {
      qc.clear();
      qc.setQueryData(['auth'], true);
      navigate(from, { replace: true });
    } else if (step.step === 'mfa') {
      setMfaHint(
        step.method === 'totp'
          ? 'Enter the 6-digit code from your authenticator app.'
          : `Enter the code we sent${step.destination ? ` to ${step.destination}` : ''}.`,
      );
      setCode('');
      setMode('mfa');
    } else {
      setMode('new_password');
    }
  };

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (mode === 'sign_in') run(async () => handleStep(await auth.signIn(email.trim(), password)));
    if (mode === 'mfa') run(async () => handleStep(await auth.confirmMfa(code.trim())));
    if (mode === 'new_password') run(async () => handleStep(await auth.completeNewPassword(newPassword)));
    if (mode === 'forgot') {
      run(async () => {
        const r = await auth.forgotPassword(email.trim());
        setNotice(`We sent a reset code${r.destination ? ` to ${r.destination}` : ''}.`);
        setMode('reset');
      });
    }
    if (mode === 'reset') {
      run(async () => {
        await auth.confirmForgotPassword(email.trim(), code.trim(), newPassword);
        setNotice('Password updated. Sign in with your new password.');
        setPassword('');
        setMode('sign_in');
      });
    }
  };

  const titles: Record<Mode, string> = {
    sign_in: 'Sign in',
    mfa: 'Check your code',
    new_password: 'Choose a new password',
    forgot: 'Reset your password',
    reset: 'Enter your reset code',
  };
  const submitLabel: Record<Mode, string> = {
    sign_in: 'Sign in',
    mfa: 'Verify',
    new_password: 'Save password',
    forgot: 'Send reset code',
    reset: 'Update password',
  };

  return (
    <div className="auth-page">
      <form className="auth-card" onSubmit={onSubmit} noValidate>
        <div className="wordmark" aria-label="DemandPilot">Demand<span>Pilot</span></div>
        <h1 style={{ fontSize: 24, marginBottom: 6 }}>{titles[mode]}</h1>
        <p className="muted small" style={{ marginBottom: 18 }}>
          {mode === 'sign_in' && 'Demand and revenue forecasts for your food business.'}
          {mode === 'mfa' && mfaHint}
          {mode === 'new_password' && 'Your account needs a new password before you continue.'}
          {mode === 'forgot' && 'We’ll email you a code to set a new password.'}
          {mode === 'reset' && 'Use the 6-digit code from the email and pick a new password.'}
        </p>
        {notice && <p className="form-success" role="status">{notice}</p>}
        {error && <p className="form-error" role="alert">{error}</p>}

        {(mode === 'sign_in' || mode === 'forgot' || mode === 'reset') && (
          <div className="field">
            <label htmlFor="email">Email</label>
            <input id="email" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus disabled={mode === 'reset'} />
          </div>
        )}
        {mode === 'sign_in' && (
          <div className="field">
            <label htmlFor="password">Password</label>
            <input id="password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          </div>
        )}
        {(mode === 'mfa' || mode === 'reset') && (
          <div className="field">
            <label htmlFor="code">{mode === 'mfa' ? 'Verification code' : 'Reset code'}</label>
            <input id="code" type="text" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]*" maxLength={8} value={code} onChange={(e) => setCode(e.target.value)} required autoFocus />
          </div>
        )}
        {(mode === 'new_password' || mode === 'reset') && (
          <div className="field">
            <label htmlFor="new-password">New password</label>
            <input id="new-password" type="password" autoComplete="new-password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required />
            <span className="hint">At least 8 characters, with a number and a symbol.</span>
          </div>
        )}

        <button type="submit" className="btn btn-primary" style={{ width: '100%', minHeight: 42 }} disabled={busy}>
          {busy ? 'Please wait…' : submitLabel[mode]}
        </button>

        <div style={{ marginTop: 14, fontSize: 14 }}>
          {mode === 'sign_in' && (
            <div style={{ display: 'grid', gap: 8 }}>
              <button type="button" className="link-btn" onClick={() => { setMode('forgot'); setError(null); setNotice(null); }}>
                Forgot your password?
              </button>
              <span className="muted">New to DemandPilot? <Link to="/sign-up">Start a free 30-day trial</Link></span>
            </div>
          )}
          {mode !== 'sign_in' && (
            <button type="button" className="link-btn" onClick={() => { setMode('sign_in'); setError(null); }}>
              Back to sign in
            </button>
          )}
        </div>
        {mock && (
          <p className="muted small" style={{ marginTop: 16, marginBottom: 0 }}>
            Mock mode: any email and password works. Use the password <code>mfa</code> to try the code step (code <code>123456</code>).
          </p>
        )}
      </form>
    </div>
  );
}
